import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { 
  ArrowLeft, 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar, 
  CreditCard,
  Key,
  Info,
  AlertCircle,
  Check,
  Eye,
  EyeOff
} from 'lucide-react';
import { alterarSenhaCliente } from '../services/api';
import { useToast } from '../hooks/use-toast';

const Perfil = ({ user }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Estados para alteração de senha
  const [senhaModal, setSenhaModal] = useState(false);
  const [senhaAtual, setSenhaAtual] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [mostrarSenhaAtual, setMostrarSenhaAtual] = useState(false);
  const [mostrarNovaSenha, setMostrarNovaSenha] = useState(false);
  const [mostrarConfirmarSenha, setMostrarConfirmarSenha] = useState(false);
  const [loadingAlterarSenha, setLoadingAlterarSenha] = useState(false);

  if (!user?.isLoggedIn) {
    navigate('/login');
    return null;
  }

  const formatarData = (data) => {
    if (!data) return '';
    try {
      return new Date(data).toLocaleDateString('pt-BR');
    } catch {
      return data;
    }
  };

  const formatarTelefone = (telefone) => {
    if (!telefone) return '';
    return telefone.replace(/(\d{2})(\d{4,5})(\d{4})/, '($1) $2-$3');
  };

  const formatarCEP = (cep) => {
    if (!cep) return '';
    return cep.replace(/(\d{5})(\d{3})/, '$1-$2');
  };

  const handleAlterarSenha = async () => {
    if (!novaSenha || novaSenha.length < 4) {
      toast({
        title: "Senha inválida",
        description: "A nova senha deve ter pelo menos 4 caracteres",
        variant: "destructive"
      });
      return;
    }

    if (novaSenha !== confirmarSenha) {
      toast({
        title: "Senhas não coincidem",
        description: "A nova senha e a confirmação devem ser iguais",
        variant: "destructive"
      });
      return;
    }

    setLoadingAlterarSenha(true);

    try {
      await alterarSenhaCliente(user.clienteId, novaSenha);
      
      toast({
        title: "Senha alterada!",
        description: "Sua senha foi alterada com sucesso.",
        duration: 3000
      });

      // Limpar campos e fechar modal
      setSenhaAtual('');
      setNovaSenha('');
      setConfirmarSenha('');
      setSenhaModal(false);

    } catch (err) {
      console.error('Erro ao alterar senha:', err);
      toast({
        title: "Erro ao alterar senha",
        description: err.message,
        variant: "destructive"
      });
    } finally {
      setLoadingAlterarSenha(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gray-50 text-gray-900 py-16 border-b-4 border-[rgb(25,42,86)]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              className="text-[rgb(25,42,86)] hover:bg-[rgb(25,42,86)] hover:text-white mb-4 font-bold"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar
            </Button>
          </div>
          
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <User className="h-12 w-12 mr-4 text-[rgb(25,42,86)]" />
              <h1 className="text-3xl md:text-4xl font-black text-[rgb(25,42,86)]">
                Meu Perfil
              </h1>
            </div>
            <p className="text-xl opacity-80 font-semibold">
              Visualize e gerencie suas informações pessoais
            </p>
          </div>
        </div>
      </div>

      {/* Conteúdo do Perfil */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          {/* Informações Pessoais */}
          <Card className="border-2 hover:border-[rgb(25,42,86)] transition-all duration-200">
            <CardHeader>
              <CardTitle className="flex items-center text-[rgb(25,42,86)]">
                <User className="h-5 w-5 mr-2" />
                Informações Pessoais
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-600">Nome Completo</Label>
                  <p className="text-gray-800 font-medium">{user.detalhes?.nomeCompleto || 'Não informado'}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-600">CPF</Label>
                  <p className="text-gray-800 font-mono">{user.detalhes?.cpf || 'Não informado'}</p>
                </div>
                {user.detalhes?.cnpj && (
                  <div>
                    <Label className="text-sm font-medium text-gray-600">CNPJ</Label>
                    <p className="text-gray-800 font-mono">{user.detalhes.cnpj}</p>
                  </div>
                )}
                <div>
                  <Label className="text-sm font-medium text-gray-600">RG</Label>
                  <p className="text-gray-800">{user.detalhes?.rg || 'Não informado'}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-600">Data de Nascimento</Label>
                  <p className="text-gray-800">{formatarData(user.detalhes?.dataNascimento) || 'Não informado'}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-600">Data de Cadastro</Label>
                  <p className="text-gray-800">{formatarData(user.detalhes?.dataCadastro) || 'Não informado'}</p>
                </div>
              </div>

              {(user.detalhes?.mae || user.detalhes?.pai) && (
                <div className="border-t pt-4">
                  <h4 className="font-medium text-gray-700 mb-2">Filiação</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {user.detalhes?.mae && (
                      <div>
                        <Label className="text-sm font-medium text-gray-600">Mãe</Label>
                        <p className="text-gray-800">{user.detalhes.mae}</p>
                      </div>
                    )}
                    {user.detalhes?.pai && (
                      <div>
                        <Label className="text-sm font-medium text-gray-600">Pai</Label>
                        <p className="text-gray-800">{user.detalhes.pai}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Informações de Contato */}
          <Card className="border-2 hover:border-[rgb(25,42,86)] transition-all duration-200">
            <CardHeader>
              <CardTitle className="flex items-center text-[rgb(238,29,37)]">
                <Mail className="h-5 w-5 mr-2" />
                Informações de Contato
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                {user.detalhes?.email && (
                  <div>
                    <Label className="text-sm font-medium text-gray-600">Email Principal</Label>
                    <p className="text-gray-800">{user.detalhes.email}</p>
                  </div>
                )}
                {user.detalhes?.email2 && (
                  <div>
                    <Label className="text-sm font-medium text-gray-600">Email Secundário</Label>
                    <p className="text-gray-800">{user.detalhes.email2}</p>
                  </div>
                )}
                {user.detalhes?.telefoneResidencial && (
                  <div>
                    <Label className="text-sm font-medium text-gray-600">Telefone Residencial</Label>
                    <p className="text-gray-800">{formatarTelefone(user.detalhes.telefoneResidencial)}</p>
                  </div>
                )}
                {user.detalhes?.telefoneComercial && (
                  <div>
                    <Label className="text-sm font-medium text-gray-600">Telefone Comercial</Label>
                    <p className="text-gray-800">{formatarTelefone(user.detalhes.telefoneComercial)}</p>
                  </div>
                )}
                {user.detalhes?.celular1 && (
                  <div>
                    <Label className="text-sm font-medium text-gray-600">Celular 1</Label>
                    <p className="text-gray-800">{formatarTelefone(user.detalhes.celular1)}</p>
                  </div>
                )}
                {user.detalhes?.celular2 && (
                  <div>
                    <Label className="text-sm font-medium text-gray-600">Celular 2</Label>
                    <p className="text-gray-800">{formatarTelefone(user.detalhes.celular2)}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Endereço */}
          <Card className="border-2 hover:border-[rgb(25,42,86)] transition-all duration-200">
            <CardHeader>
              <CardTitle className="flex items-center text-green-600">
                <MapPin className="h-5 w-5 mr-2" />
                Endereço
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {user.detalhes?.endereco && (
                <div>
                  <Label className="text-sm font-medium text-gray-600">Logradouro</Label>
                  <p className="text-gray-800">{user.detalhes.endereco}</p>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {user.detalhes?.bairro && (
                  <div>
                    <Label className="text-sm font-medium text-gray-600">Bairro</Label>
                    <p className="text-gray-800">{user.detalhes.bairro}</p>
                  </div>
                )}
                {user.detalhes?.cep && (
                  <div>
                    <Label className="text-sm font-medium text-gray-600">CEP</Label>
                    <p className="text-gray-800 font-mono">{formatarCEP(user.detalhes.cep)}</p>
                  </div>
                )}
                {user.detalhes?.cidade && (
                  <div>
                    <Label className="text-sm font-medium text-gray-600">Cidade</Label>
                    <p className="text-gray-800">{user.detalhes.cidade}</p>
                  </div>
                )}
                {user.detalhes?.estado && (
                  <div>
                    <Label className="text-sm font-medium text-gray-600">Estado</Label>
                    <p className="text-gray-800">{user.detalhes.estado}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Informações Adicionais */}
          <Card className="border-2 hover:border-[rgb(25,42,86)] transition-all duration-200">
            <CardHeader>
              <CardTitle className="flex items-center text-purple-600">
                <Info className="h-5 w-5 mr-2" />
                Informações Adicionais
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-600">ID Cliente</Label>
                  <p className="text-gray-800 font-mono">#{user.clienteId}</p>
                </div>
                {user.detalhes?.classificacao && (
                  <div>
                    <Label className="text-sm font-medium text-gray-600">Classificação</Label>
                    <p className="text-gray-800">{user.detalhes.classificacao}</p>
                  </div>
                )}
                {user.detalhes?.profissao && (
                  <div>
                    <Label className="text-sm font-medium text-gray-600">Profissão</Label>
                    <p className="text-gray-800">{user.detalhes.profissao}</p>
                  </div>
                )}
                {user.detalhes?.empresa && (
                  <div>
                    <Label className="text-sm font-medium text-gray-600">Empresa</Label>
                    <p className="text-gray-800">{user.detalhes.empresa}</p>
                  </div>
                )}
              </div>

              {user.detalhes?.observacoes && (
                <div className="border-t pt-3">
                  <Label className="text-sm font-medium text-gray-600">Observações</Label>
                  <p className="text-gray-800 text-sm bg-gray-50 p-2 rounded">{user.detalhes.observacoes}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Ações */}
        <div className="mt-8 flex justify-center">
          <Dialog open={senhaModal} onOpenChange={setSenhaModal}>
            <DialogTrigger asChild>
              <Button className="bg-[rgb(238,29,37)] hover:bg-[rgb(238,29,37)]/90">
                <Key className="h-4 w-4 mr-2" />
                Alterar Senha
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center text-[rgb(25,42,86)]">
                  <Key className="h-5 w-5 mr-2" />
                  Alterar Senha
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="novaSenha">Nova Senha</Label>
                  <div className="relative">
                    <Input
                      id="novaSenha"
                      type={mostrarNovaSenha ? 'text' : 'password'}
                      value={novaSenha}
                      onChange={(e) => setNovaSenha(e.target.value)}
                      placeholder="Digite sua nova senha"
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setMostrarNovaSenha(!mostrarNovaSenha)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {mostrarNovaSenha ? (
                        <EyeOff className="h-4 w-4 text-gray-400" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirmarSenha">Confirmar Nova Senha</Label>
                  <div className="relative">
                    <Input
                      id="confirmarSenha"
                      type={mostrarConfirmarSenha ? 'text' : 'password'}
                      value={confirmarSenha}
                      onChange={(e) => setConfirmarSenha(e.target.value)}
                      placeholder="Confirme sua nova senha"
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setMostrarConfirmarSenha(!mostrarConfirmarSenha)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {mostrarConfirmarSenha ? (
                        <EyeOff className="h-4 w-4 text-gray-400" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>

                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    A nova senha deve ter pelo menos 4 caracteres.
                  </AlertDescription>
                </Alert>

                <div className="flex justify-end space-x-2">
                  <Button
                    variant="outline"
                    onClick={() => setSenhaModal(false)}
                    disabled={loadingAlterarSenha}
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleAlterarSenha}
                    disabled={loadingAlterarSenha}
                    className="bg-[rgb(25,42,86)] hover:bg-[rgb(25,42,86)]/90 font-bold"
                  >
                    {loadingAlterarSenha ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Alterando...
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <Check className="h-4 w-4 mr-2" />
                        Alterar Senha
                      </div>
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
};

export default Perfil;